import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, Wrench, Truck } from "lucide-react";
import Calculator from "@/components/Calculator";
import { api } from "@/lib/api";

const SERVICE_CATS = [
  {
    icon: <Truck size={20} />,
    title: "Tradie Utes",
    desc: "HiLux, Ranger, and other workhorses ready to earn from day one.",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Family SUVs",
    desc: "Sportage, CX-5, Tucson — safe, spacious, and warranty-backed.",
  },
  {
    icon: <FileText size={20} />,
    title: "Daily Drivers",
    desc: "Corolla, i30, Mazda3 — efficient, reliable, low running costs.",
  },
  {
    icon: <Wrench size={20} />,
    title: "Service Included",
    desc: "Registration and QBE insurance bundled into your term.",
  },
];

export default function Services() {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-6 fade-up">Our Services</div>
          <h1
            className="font-display text-white max-w-4xl leading-[1.05] fade-up fade-up-delay-1"
            style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)", fontWeight: 300 }}
          >
            The full <span className="italic text-[#D4AF37]">rent-to-own</span> programme — built around your business.
          </h1>
          <p className="mt-8 text-white/65 max-w-2xl text-lg leading-relaxed fade-up fade-up-delay-2">
            Choose your vehicle, set your term, and lock in flexible payments. Everything from
            insurance to warranty is handled — so you focus on the drive.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {SERVICE_CATS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-cat-${i}`}
                className="bg-[#0a0a0a] p-8 hover:bg-[#0E0E0E] transition-colors"
              >
                <div className="text-[#D4AF37] mb-6">{s.icon}</div>
                <h3 className="text-white text-lg mb-3 font-medium">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Fleet */}
      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-4">The Fleet</div>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-14 max-w-2xl" style={{ fontWeight: 300 }}>
            Brand-new <span className="italic text-[#D4AF37]">2026 vehicles</span> in stock now.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => (
              <article
                key={v.id}
                data-testid={`service-vehicle-${v.id}`}
                className="group border border-white/10 bg-[#0a0a0a] overflow-hidden hover:border-[#D4AF37]/40 transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#111]">
                  <img
                    src={v.image}
                    alt={`${v.make} ${v.model}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-[0.18em] text-white/40">
                      {v.type} · {v.year}
                    </span>
                    <span className="text-xs text-[#D4AF37]">
                      ${v.deposit_from.toLocaleString()} dep.
                    </span>
                  </div>
                  <h3 className="text-white text-xl mb-1">
                    {v.make} <span className="text-white/60">{v.model}</span>
                  </h3>
                  <p className="text-sm text-white/45">
                    Drive-away ${v.drive_away_price.toLocaleString()}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {v.highlights?.map((h) => (
                      <span
                        key={h}
                        className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/10 text-white/60"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/5 flex items-baseline justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                        From
                      </div>
                      <div className="font-display text-3xl text-[#D4AF37]" style={{ fontWeight: 400 }}>
                        ${v.weekly_from}
                        <span className="text-sm text-white/50 ml-1">/wk</span>
                      </div>
                    </div>
                    <Link
                      to="/apply"
                      data-testid={`service-apply-${v.id}`}
                      className="text-xs uppercase tracking-[0.18em] text-white hover:text-[#D4AF37] transition-colors"
                    >
                      Apply →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="label-eyebrow mb-4">Plan it</div>
            <h2 className="font-display text-4xl md:text-5xl text-white" style={{ fontWeight: 300 }}>
              Your weekly figure, in <span className="italic text-[#D4AF37]">seconds</span>.
            </h2>
          </div>
          <Calculator />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-white text-4xl md:text-5xl" style={{ fontWeight: 300 }}>
            Found your vehicle?
          </h2>
          <Link
            to="/apply"
            data-testid="services-cta-apply"
            className="inline-flex mt-10 items-center gap-2 bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-wide font-medium hover:bg-[#F6ECCC] transition-colors"
          >
            Apply Now <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
